import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Brain, Info, Timer, Activity, Heart } from 'lucide-react';
import { StressGauge } from './StressGauge';
import { StressScoreInfo } from './StressScoreInfo';
import { useHealthMetrics } from '../../hooks/useHealthMetrics';
import { useNavigate } from 'react-router-dom';
import { BreathingExercise } from './breathing/BreathingExercise';

export function StressMonitor() {
  const [showBreathing, setShowBreathing] = useState(false);
  const metrics = useHealthMetrics();
  const navigate = useNavigate();

  const stressLevel = (metrics.stress.current / 100) * 3;
  const stressLabel = stressLevel < 1 ? 'Low' : stressLevel < 2 ? 'Medium' : 'High';

  const handleClose = () => {
    navigate('/home');
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Brain className="w-6 h-6 text-primary animate-pulse" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
            </div>
            <div>
              <h2 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-400 to-blue-500">
                Stress Monitor
              </h2>
              <p className="text-sm text-muted-foreground">Track and manage your stress levels</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-8">
            {/* Stress Gauge */}
            <div className="theme-card p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
              <div className="relative">
                <StressGauge 
                  value={stressLevel}
                  label={stressLabel}
                  lastUpdated="Just now"
                />
              </div>
            </div>

            {/* Quick Action */}
            <div className="theme-card p-6 bg-gradient-to-br from-background/50 to-background/30 backdrop-blur-md border border-primary/10">
              <h3 className="text-lg font-medium mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-400 to-blue-500">
                Quick Action
              </h3>
              {showBreathing ? (
                <BreathingExercise onComplete={() => setShowBreathing(false)} />
              ) : (
                <button
                  onClick={() => setShowBreathing(true)}
                  className="w-full py-3 rounded-lg relative group overflow-hidden bg-gradient-to-r from-primary via-purple-500 to-blue-500 text-white font-medium"
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative">Start Breathing Exercise</span>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-8">
            {/* Metrics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="theme-card p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Heart className="w-4 h-4 text-primary" />
                    <span>Heart Rate</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.heartRate.current}</div>
                  <div className="text-xs text-muted-foreground mt-1">bpm</div>
                </div>
              </div>

              <div className="theme-card p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Activity className="w-4 h-4 text-primary" />
                    <span>Recovery</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.recovery}%</div>
                  <div className="text-xs text-success mt-1">Optimal range</div>
                </div>
              </div>

              <div className="theme-card p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Brain className="w-4 h-4 text-primary" />
                    <span>HRV</span>
                  </div>
                  <div className="text-2xl font-bold">{metrics.hrv.current}</div>
                  <div className="text-xs text-muted-foreground mt-1">ms</div>
                </div>
              </div>
            </div>

            {/* Stress Score Info */}
            <StressScoreInfo />

            {/* Status Message */}
            <div className={`theme-card p-4 bg-gradient-to-r ${
              stressLevel > 2 ? 'from-red-500/10 via-red-500/5 to-transparent border-red-500/10' : 
              stressLevel < 1 ? 'from-green-500/10 via-green-500/5 to-transparent border-green-500/10' : 
              'from-blue-500/10 via-blue-500/5 to-transparent border-blue-500/10'
            } border backdrop-blur-md`}>
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 shrink-0" />
                <p className="text-sm">
                  {stressLevel > 2 ? 'Consider taking a breathing break to reduce stress levels' :
                   stressLevel < 1 ? 'Your stress levels are optimal - great job!' :
                   'Moderate stress levels - maintain balance with regular breaks'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}